import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { IfConditionActivity } from 'app/model/pipeline/if-condition.activity';
import { Metamodel } from 'app/model/base/model';
import { Pipeline } from 'app/model/pipeline/pipeline';
import { Owned } from 'app/model/base/named';
import { ActivityAsyncAdapter } from 'app/model/adapters/activities/activity.adapter';

import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { generateUUID } from 'app/shared/utils/uuid.util';
import { Activity, arrayIsNullOrEmpty, ModelConstants } from 'shell-models-lib';
import {
    IIfConditionActivityInterimContext,
    IPipelineCopilotCurrentPipelineContext,
    PipelineActivityInterimContext
} from 'app/interfaces/copilot.pipeline.action.types';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(IfConditionActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        type: '*',
        description: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            expression: '*',
            ifTrueActivities: '*',
            ifFalseActivities: '*'
        }
    }
})
export class IfConditionActivityAdapter extends ActivityAsyncAdapter<
    IfConditionActivity,
    Model.IfConditionActivity,
    IIfConditionActivityInterimContext
> {
    public override async toFabricCopilotContext(
        ifConditionActivity: IfConditionActivity
    ): Promise<IIfConditionActivityInterimContext> {
        if (ifConditionActivity) {
            const ifConditionActivityContext: IIfConditionActivityInterimContext = {
                ...(await super.toFabricCopilotContext(ifConditionActivity)),
                Expression: IfConditionActivity.EXPRESSION.templateValue(ifConditionActivity)
            };

            const trueChildActivities: PipelineActivityInterimContext[] = [];
            const falseChildActivities: PipelineActivityInterimContext[] = [];
            for (const activity of ifConditionActivity.ifTrueActivityPipeline?.activities ?? []) {
                const adapter = this.context.asyncAdapterFor<Activity, Model.Activity, PipelineActivityInterimContext>(
                    Metamodel.modelOf(activity)
                );
                const activityContext = await adapter.toFabricCopilotContext(activity);
                if (activityContext) {
                    trueChildActivities.push(activityContext);
                }
            }
            if (trueChildActivities.length > 0) {
                ifConditionActivityContext.TrueChildActivities = trueChildActivities;
            }
            for (const activity of ifConditionActivity.ifFalseActivityPipeline?.activities ?? []) {
                const adapter = this.context.asyncAdapterFor<Activity, Model.Activity, PipelineActivityInterimContext>(
                    Metamodel.modelOf(activity)
                );
                const activityContext = await adapter.toFabricCopilotContext(activity);
                if (activityContext) {
                    falseChildActivities.push(activityContext);
                }
            }
            if (falseChildActivities.length > 0) {
                ifConditionActivityContext.FalseChildActivities = falseChildActivities;
            }
            return ifConditionActivityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IIfConditionActivityInterimContext,
        model?: IfConditionActivity
    ): Promise<IfConditionActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.Expression != null) {
                IfConditionActivity.EXPRESSION.setTemplateValue(model, context.Expression);
            }
            const ifActivityPipelineName = generateUUID();
            if (!model.ifTrueActivityPipeline) {
                model.ifTrueActivityPipeline = Owned.create<Pipeline>(Pipeline.MODEL, model);
                model.ifTrueActivityPipeline.name = ifActivityPipelineName + ModelConstants.ifActivityTrueSuffix;
            }
            if (!arrayIsNullOrEmpty(context.TrueChildActivities)) {
                const adapter = this.context.asyncAdapterFor<
                    Pipeline,
                    Model.PipelineResource,
                    IPipelineCopilotCurrentPipelineContext
                >(Pipeline.MODEL);
                await adapter.fromFabricCopilotContext(
                    {
                        Activities: context.TrueChildActivities
                    },
                    model.ifTrueActivityPipeline
                );
            } else {
                model.ifTrueActivityPipeline.activities.clear();
            }
            if (!model.ifFalseActivityPipeline) {
                model.ifFalseActivityPipeline = Owned.create<Pipeline>(Pipeline.MODEL, model);
                model.ifFalseActivityPipeline.name = ifActivityPipelineName + ModelConstants.ifActivityFalseSuffix;
            }
            if (!arrayIsNullOrEmpty(context.FalseChildActivities)) {
                const adapter = this.context.asyncAdapterFor<
                    Pipeline,
                    Model.PipelineResource,
                    IPipelineCopilotCurrentPipelineContext
                >(Pipeline.MODEL);
                await adapter.fromFabricCopilotContext(
                    {
                        Activities: context.FalseChildActivities
                    },
                    model.ifFalseActivityPipeline
                );
            } else {
                model.ifFalseActivityPipeline.activities.clear();
            }
        }
        return model;
    }

    public override async postToModel(activity: IfConditionActivity): Promise<void> {
        await super.postToModel(activity);

        await Promise.all(
            Array.from(activity.ifTrueActivityPipeline.activities.values()).map(async (innerActivity) => {
                const activityModel = Metamodel.modelOf(innerActivity);
                const adapter = this.context.isAsyncAdapter(activityModel)
                    ? this.context.asyncAdapterFor(activityModel)
                    : this.context.adapterFor(activityModel);
                try {
                    await adapter?.postToModel(innerActivity);
                } catch (e) {
                    this._logError(
                        `Activity "${innerActivity.name}" \`postToModel\` failed in ifTrueActivity activity of pipeline "${activity.ifTrueActivityPipeline.name}"`,
                        e
                    );
                }
            })
        );

        await Promise.all(
            Array.from(activity.ifFalseActivityPipeline.activities.values()).map(async (innerActivity) => {
                const activityModel = Metamodel.modelOf(innerActivity);
                const adapter = this.context.isAsyncAdapter(activityModel)
                    ? this.context.asyncAdapterFor(activityModel)
                    : this.context.adapterFor(activityModel);
                try {
                    await adapter?.postToModel(innerActivity);
                } catch (e) {
                    this._logError(
                        `Activity "${innerActivity.name}" \`postToModel\` failed in ifFalseActivity activity of pipeline "${activity.ifFalseActivityPipeline.name}"`,
                        e
                    );
                }
            })
        );
    }

    protected async _toResource(activity: IfConditionActivity): Promise<Model.IfConditionActivity> {
        const modelIfConditionActivity: Model.IfConditionActivity = super._getCommonActivityProperties(
            activity,
            'IfCondition'
        ) as Model.IfConditionActivity;

        modelIfConditionActivity.typeProperties = modelIfConditionActivity.typeProperties || { expression: undefined };

        if (!ValueAdapter.isTemplated(activity, IfConditionActivity.EXPRESSION)) {
            // the expression propery only accepts expressions, turn the regular value into an expresion
            IfConditionActivity.EXPRESSION.setTemplateValue(activity, IfConditionActivity.EXPRESSION.value(activity));
        }

        modelIfConditionActivity.typeProperties.expression = ValueAdapter.toResource(
            activity,
            IfConditionActivity.EXPRESSION
        );

        const adapter = this.context.asyncAdapterFor(Pipeline.MODEL);

        if (adapter) {
            const falsePipeline = await adapter.toResource(activity.ifFalseActivityPipeline);
            if (falsePipeline.properties) {
                modelIfConditionActivity.typeProperties.ifFalseActivities = falsePipeline.properties.activities;
            }
            const truePipeline = await adapter.toResource(activity.ifTrueActivityPipeline);
            if (truePipeline.properties) {
                modelIfConditionActivity.typeProperties.ifTrueActivities = truePipeline.properties.activities;
            }
        }

        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelIfConditionActivity, parsed);
        }

        return modelIfConditionActivity;
    }

    protected async _toModel(modelActivity: Model.IfConditionActivity): Promise<IfConditionActivity> {
        const activity: IfConditionActivity = IfConditionActivity.MODEL.create<IfConditionActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);
        modelActivity.typeProperties = modelActivity.typeProperties || { expression: undefined };
        ValueAdapter.toModel(activity, IfConditionActivity.EXPRESSION, modelActivity.typeProperties.expression);

        const adapter = this.context.asyncAdapterFor(Pipeline.MODEL);

        if (adapter) {
            const ifActivityPipelineName = generateUUID();
            const tempFalsePipeline: Model.PipelineResource = {
                properties: { activities: modelActivity.typeProperties.ifFalseActivities }
            };
            activity.ifFalseActivityPipeline = Owned.create<Pipeline>(Pipeline.MODEL, activity);
            await adapter.toModel(tempFalsePipeline, activity.ifFalseActivityPipeline);
            // Nested pipeline name is unnecessary for If Condition model, it's just used as a workaround of Pipeline naming validator.
            activity.ifFalseActivityPipeline.name = ifActivityPipelineName + ModelConstants.ifActivityFalseSuffix;

            const tempTruePipeline: Model.PipelineResource = {
                properties: { activities: modelActivity.typeProperties.ifTrueActivities }
            };
            activity.ifTrueActivityPipeline = Owned.create<Pipeline>(Pipeline.MODEL, activity);
            await adapter.toModel(tempTruePipeline, activity.ifTrueActivityPipeline);
            // Nested pipeline name is unnecessary for If Condition model, it's just used as a workaround of Pipeline naming validator.
            activity.ifTrueActivityPipeline.name = ifActivityPipelineName + ModelConstants.ifActivityTrueSuffix;
        }

        activity.raw = JSON.stringify(
            await ValueAdapter.remainder(this.context, modelActivity, IfConditionActivity.MODEL)
        );
        return activity;
    }
}
